import React, { useMemo, useState } from "react"
import { LoadingIndicator } from "components/LoadingIndicator"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import { columnDef } from "./experienceTableColumns"
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table"
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import DraggableRow from "./DraggableRow"
import { customRowModel } from "./customRowModel"
import {
  updateCategorizedEventsOrder,
  updateMultipleCategoriesOrder,
} from "store/actions"

const ExpTableWithCategories = ({
  tableData,
  isEventsLoaded,
  rowSelection,
  setRowSelection,
  publishChangeHandler,
  history,
  handleDeleteEventAction,
  updateCategorizedEventsOrder,
  vendorId,
  updateMultipleCategoriesOrder,
  onClickCloneExpAction,
}) => {
  const dataIds = useMemo(() => tableData?.map(({ id }) => id), [tableData])
  const finalColumnDef = useMemo(() => columnDef, [])
  const [sorting, setSorting] = useState([])
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const actions = [
    {
      name: "Analytics",
      onClick: item => {
        history.push(`/event-reports/${item?.id}`)
      },
      icon: "bx bx-line-chart",
      isAvailable: true,
    },
    {
      name: "Edit",
      onClick: item => {
        history.push(`/events/${item.vendor_id}/${item.id}`)
      },
      icon: "bx bx-edit",
      isAvailable: true,
    },
    {
      name: "Clone",
      onClick: item => {
        onClickCloneExpAction(item)
      },
      icon: "bx bx-copy",
      isAvailable: true,
    },
    {
      name: "Delete",
      onClick: item => {
        handleDeleteEventAction(item)
      },
      icon: "bx bx-trash",
      isAvailable: true,
    },
  ]

  const handleDragEnd = event => {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id)
      const newIndex = dataIds.indexOf(over.id)
      const modifiedData = arrayMove(tableData, oldIndex, newIndex)
      const data = modifiedData
        .map((category, position) => {
          if (
            modifiedData[position].id !== dataIds[position] ||
            modifiedData[position].order === 0
          ) {
            return {
              vendor_id: category?.vendor_id,
              name: category?.name,
              type: category?.type,
              inventory: category?.inventory || 0,
              business_unit_id: category?.business_unit_id,
              id: category.id,
              order: position + 1,
              delete_file: 0,
              delete_banner: 0,
            }
          } else {
            return null
          }
        })
        .filter(category => category)
      updateMultipleCategoriesOrder(data)
    }
  }

  const handleDragEndSub = event => {
    const { active, over } = event
    const categoryId = active?.data?.current?.parentId

    if (active?.id !== over?.id) {
      const originalData = tableData?.find(
        item => item?.id === categoryId
      )?.subRows
      const oldIndex = originalData?.findIndex(i => i.id === active.id)
      const newIndex = originalData?.findIndex(i => i.id === over.id)
      const modifiedData = arrayMove(originalData, oldIndex, newIndex)
      const data = modifiedData
        .map((item, position) => {
          if (
            modifiedData[position].id !== originalData[position].id ||
            modifiedData[position].order === 0
          ) {
            return {
              event_id: item.id,
              order: position + 1,
            }
          } else {
            return null
          }
        })
        .filter(item => item)

      updateCategorizedEventsOrder({
        category_id: categoryId,
        events: data,
        vendor_id: vendorId,
      })
    }
  }

  const tableInstance = useReactTable({
    columns: finalColumnDef,
    data: tableData,
    getRowId: row => row?.id,
    meta: { actions, publishChangeHandler },
    enableRowSelection: row => row?.depth > 0,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: customRowModel(),
    state: {
      sorting: sorting,
      expanded: true,
      rowSelection,
    },
    onSortingChange: setSorting,
    getSubRows: row => row.subRows,
    getExpandedRowModel: getExpandedRowModel(),
  })

  return (
    <React.Fragment>
      {!isEventsLoaded ? (
        <LoadingIndicator />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div>
            <table className="w-full divide-y divide-[#D4D4D8]">
              <thead className="bg-white bg-white divide-y divide-[#D2E1EF] border-t-0">
                {tableInstance.getHeaderGroups().map(headerEl => {
                  return (
                    <tr key={headerEl.id}>
                      {headerEl.headers.map(columnEl => {
                        return (
                          <th
                            key={columnEl.id}
                            colSpan={columnEl.colSpan}
                            scope="col"
                            onClick={columnEl.column.getToggleSortingHandler()}
                          >
                            {columnEl.isPlaceholder ? null : (
                              <span
                                style={
                                  columnEl?.column?.getCanSort()
                                    ? { cursor: "pointer" }
                                    : {}
                                }
                              >
                                {flexRender(
                                  columnEl.column.columnDef.header,
                                  columnEl.getContext()
                                )}
                                {columnEl?.column?.getCanSort() &&
                                  ({
                                    asc: (
                                      <i className="mdi mdi-sort-ascending ms-1 font-size-16 align-middle" />
                                    ),
                                    desc: (
                                      <i className="mdi mdi-sort-descending ms-1 font-size-16 align-middle" />
                                    ),
                                  }[columnEl?.column?.getIsSorted()] ?? (
                                    <i className="mdi mdi-sort ms-1 font-size-16 align-middle text-secondary" />
                                  ))}
                              </span>
                            )}
                          </th>
                        )
                      })}
                    </tr>
                  )
                })}
              </thead>
              <tbody className="bg-white divide-y divide-[#D2E1EF]">
                <SortableContext
                  id="categories"
                  items={dataIds}
                  strategy={verticalListSortingStrategy}
                >
                  {tableInstance.getPreExpandedRowModel().rows.map(rowEl => {
                    return (
                      <React.Fragment key={rowEl?.id}>
                        <DraggableRow
                          key={rowEl?.id}
                          row={rowEl}
                          isCategory={true}
                        />
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEndSub}
                        >
                          <SortableContext
                            id="experience"
                            items={rowEl?.originalSubRows?.map(
                              item => item?.id
                            )}
                            strategy={verticalListSortingStrategy}
                          >
                            {rowEl?.subRows?.length > 0 &&
                              rowEl?.subRows?.map(subRow => {
                                return (
                                  <DraggableRow
                                    key={subRow?.id}
                                    row={subRow}
                                    parentId={rowEl?.original?.id}
                                  />
                                )
                              })}
                          </SortableContext>
                        </DndContext>
                      </React.Fragment>
                    )
                  })}
                </SortableContext>
              </tbody>
            </table>
          </div>
        </DndContext>
      )}
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  isEventsLoaded: state.experiences.isEventsLoaded,
  vendorId: state.Login.vendorId,
})

const mapDispatchToProps = dispatch => ({
  updateCategorizedEventsOrder: values =>
    dispatch(updateCategorizedEventsOrder(values)),
  updateMultipleCategoriesOrder: values =>
    dispatch(updateMultipleCategoriesOrder(values)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ExpTableWithCategories))
